<template>
  <div
    class="enhance-panel-wrap"
  >
    <v-card
      elevation="0"
      class="enhance-panel"
      :class="{
        'enhance-panel--quiet': !readyToEnhance,
        'enhance-panel--ready': readyToEnhance
      }"
    >
      <div class="d-flex align-start">
        <v-avatar
          :color="readyToEnhance ? 'accent' : 'primary'"
          :size="readyToEnhance ? 40 : 32"
          class="mr-4 flex-shrink-0"
        >
          <v-icon
            color="white"
            :small="!readyToEnhance"
          >
            {{ readyToEnhance ? 'check' : 'info' }}
          </v-icon>
        </v-avatar>
        <div class="enhance-panel__body">
          <h3
            class="font-weight-bold mb-1"
            :class="readyToEnhance ? 'text-h6' : 'text-subtitle-1'"
          >
            {{ readyToEnhance ? $t('article.manualEnhance.titleReady') : $t('article.manualEnhance.title') }}
          </h3>
          <p class="mb-0 text--secondary">
            {{ readyToEnhance ? $t('article.manualEnhance.descriptionReady') : $t('article.manualEnhance.description') }}
          </p>
        </div>
      </div>
      <div class="enhance-panel__footer d-flex flex-column flex-lg-row align-stretch align-lg-center">
        <p class="caption text--secondary flex-grow-1 mb-3 mb-lg-0 mr-lg-4">
          {{ $t('article.manualEnhance.danger') }}
        </p>
        <div class="d-flex flex-column-reverse flex-sm-row align-stretch align-sm-center enhance-panel__actions">
          <template v-if="readyToEnhance">
            <v-btn
              outlined
              color="primary"
              class="text-none enhance-panel__secondary-btn"
              @click="toggleKeywordAnalysis"
            >
              <v-icon
                left
                small
              >
                {{ keywordSelectionShown ? 'expand_less' : 'expand_more' }}
              </v-icon>
              {{ $t('keywords.allKeywords') }}
            </v-btn>
            <v-btn
              depressed
              color="accent"
              class="text-none"
              :loading="enhancing"
              :disabled="!ableToEnhance"
              @click="$emit('click')"
            >
              <v-icon
                left
                small
              >
                edit_note
              </v-icon>
              {{ $t('article.manualEnhance.enhance') }}
            </v-btn>
          </template>
          <template v-else>
            <v-tooltip
              top
              :disabled="ableToEnhance"
            >
              <template #activator="{ on }">
                <span
                  class="d-inline-flex enhance-panel__secondary"
                  v-on="!ableToEnhance ? on : {}"
                >
                  <v-btn
                    text
                    color="primary"
                    class="text-none"
                    :loading="enhancing"
                    :disabled="!ableToEnhance"
                    v-on="ableToEnhance ? on : {}"
                    @click="$emit('click')"
                  >
                    <v-icon
                      left
                      small
                    >
                      edit_note
                    </v-icon>
                    {{ $t('article.manualEnhance.enhanceNow') }}
                  </v-btn>
                </span>
              </template>
              {{ keywordsForAi.length > MAX_AI_KEYWORDS ? $t('article.manualEnhance.tooltip.tooManyKeywords') : $t('article.manualEnhance.tooltip.noKeywords') }}
            </v-tooltip>
            <v-btn
              depressed
              color="accent"
              class="text-none enhance-panel__primary"
              @click="toggleKeywordAnalysis"
            >
              <v-icon left>
                {{ keywordSelectionShown ? 'expand_less' : 'expand_more' }}
              </v-icon>
              {{ $t('keywords.allKeywords') }}
            </v-btn>
          </template>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script lang="ts">
    import Vue from "vue";
    import { MAX_AI_KEYWORDS } from '@/utils/Article';
    import { KeywordForAi } from "@/model/Article";

    export default Vue.extend({
        name: "ArticleEnhancingAlert",
        props: {
            enhancing: {
                type: Boolean,
                default: false
            }
        },
        data: () => ({
            analysisReviewed: false,
            MAX_AI_KEYWORDS: MAX_AI_KEYWORDS
        }),
        computed: {
            keywordSelectionShown: {
                get(): boolean {
                    return this.$store.getters.keywordSelectionShown;
                },
                set(value: boolean) {
                    this.$store.commit('setKeywordSelectionShown', value);
                }
            },
            keywordsForAi(): KeywordForAi[] {
                return this.$store.getters.articleKeywordsForAi;
            },
            ableToEnhance(): boolean {
                return this.keywordsForAi.length <= MAX_AI_KEYWORDS && this.keywordsForAi.length > 0;
            },
            readyToEnhance(): boolean {
                return this.analysisReviewed && this.ableToEnhance;
            }
        },
        watch: {
            keywordSelectionShown(shown: boolean) {
                if (shown) {
                    this.analysisReviewed = true;
                }
            }
        },
        mounted() {
            if (this.keywordSelectionShown) {
                this.analysisReviewed = true;
            }
        },
        methods: {
            toggleKeywordAnalysis() {
                const opening = !this.keywordSelectionShown;
                this.keywordSelectionShown = opening;
                if (opening) {
                    this.analysisReviewed = true;
                    this.$nextTick(() => {
                        this.scrollToKeywordSelection();
                    });
                }
            },
            scrollToKeywordSelection() {
                const keywordTables = document.getElementById('keywordTables');
                const keywordContainer = document.getElementById('keywordSelectionContainer');
                const target = keywordTables || keywordContainer;
                if (target) {
                    target.scrollIntoView({
                        behavior: 'smooth',
                        block: 'start'
                    });
                }
            }
        }
    });
</script>

<style lang="sass" scoped>
.enhance-panel-wrap
    width: 70vw

.enhance-panel
    width: 70vw
    border-radius: 12px !important
    background: #fff !important

.enhance-panel--quiet
    padding: 14px 18px
    border: 1px solid rgba(0, 0, 0, 0.12) !important
    background: #fafafa !important

.enhance-panel--ready
    padding: 18px 22px
    border: 1px solid var(--v-accent-base) !important

.enhance-panel__body
    min-width: 0
    flex: 1

.enhance-panel__footer
    margin-top: 12px
    padding-top: 12px
    border-top: 1px solid rgba(0, 0, 0, 0.08)

.enhance-panel__actions
    flex-shrink: 0
    gap: 8px

.enhance-panel__secondary,
.enhance-panel__secondary-btn
    width: 100%

    @media (min-width: 600px)
        width: auto
        margin-right: 4px

.enhance-panel__primary,
.enhance-panel__secondary .v-btn,
.enhance-panel__secondary-btn
    width: 100%

    @media (min-width: 600px)
        width: auto
</style>
